import { ECE, PLATFORM_TALLY, PLATFORM_LIMESURVEY } from '@BUP/constants'
import { useContext, useMemo } from 'react'
import { RoleContext } from '@BUP/contexts/RoleContext'
import { useApiQuery } from '@BUP/hooks/useApiQuery'

// Tally configs live in a separate backend model/table; listing them so the
// ECE tracking view can show both platforms means a second request that the
// hook merges with the LimeSurvey list. The platform-tagging mirrors the admin
// view (useGetDiagnosisData + useGetTallyDiagnosisData).
export const useGetDiagnosisSurveys = (termId, options = {}) => {
  const role = useContext(RoleContext)
  const isRoleECE = role?.type === ECE

  const limeQuery = useApiQuery(
    ['diagnosis-surveys', termId],
    'schools',
    `/terms/${termId}/diagnostic-surveys`,
    {
      enabled: Boolean(termId && isRoleECE),
      ...options
    },
    { authenticated: true, version: 3 }
  )

  const tallyQuery = useApiQuery(
    ['tally-diagnosis-surveys', termId],
    'schools',
    `/terms/${termId}/tally-diagnostic-surveys`,
    {
      enabled: Boolean(termId && isRoleECE),
      ...options
    },
    { authenticated: true, version: 3 }
  )

  // Defaults missing platform to 'limesurvey' so pre-Tally API responses keep working unchanged.
  const diagnosisSurveys = useMemo(() => {
    if (!isRoleECE) return []

    const limeSurveys = (limeQuery.data ?? []).map(survey => ({
      ...survey,
      platform: survey?.platform ?? PLATFORM_LIMESURVEY
    }))

    // Same dedup as the admin view: only the most recent config per
    // (roleId, tallyFormId) drives tracking, so re-creating a survey is an
    // update, not a second parallel card.
    const latestTallyByKey = new Map()
    ;(tallyQuery.data ?? []).forEach((survey) => {
      const key = `${survey?.roleId ?? ''}|${survey?.tallyFormId ?? ''}`
      const current = latestTallyByKey.get(key)
      const createdAt = survey?.createdAt ? new Date(survey.createdAt).getTime() : 0
      const currentCreatedAt = current?.createdAt ? new Date(current.createdAt).getTime() : -1
      if (!current || createdAt >= currentCreatedAt) latestTallyByKey.set(key, survey)
    })

    const tallySurveys = Array.from(latestTallyByKey.values()).map(survey => ({
      ...survey,
      platform: PLATFORM_TALLY
    }))

    return [...limeSurveys, ...tallySurveys]
  }, [isRoleECE, limeQuery.data, tallyQuery.data])

  return {
    diagnosisSurveys,
    isLoadingDiagnosisSurveys: limeQuery.isLoading || tallyQuery.isLoading,
    isFetchingDiagnosisSurveys: limeQuery.isFetching || tallyQuery.isFetching,
    errorDiagnosisSurveys: limeQuery.isError
      ? limeQuery.error
      : (tallyQuery.isError ? tallyQuery.error : null),
    getDiagnosisSurveys: async () => {
      await Promise.all([limeQuery.refetch(), tallyQuery.refetch()])
    }
  }
}
